import { useState, useEffect } from "react";
import {
  Coins, Receipt, Gift, Shield, Users, Plus, RefreshCw,
  CheckCircle2, XCircle, Clock, Loader2, Download, AlertCircle, Copy,
  CreditCard, Wallet, Smartphone, Bitcoin, Settings, Save, Lock, ToggleLeft, ToggleRight,
  ExternalLink, Sparkles
} from "lucide-react";
import api from "../../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatusBadge({ status }) {
  const cfg = {
    completed: { icon: CheckCircle2, color: "#10b981", label: "Completat" },
    pending:   { icon: Clock,        color: "#f59e0b", label: "În așteptare" },
    failed:    { icon: XCircle,      color: "#ef4444", label: "Eșuat" },
    refunded:  { icon: XCircle,      color: "#8b5cf6", label: "Rambursare" },
  };
  const c = cfg[status] || cfg.pending;
  const Icon = c.icon;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-chakra font-black uppercase"
      style={{ background: `${c.color}15`, color: c.color, border: `1px solid ${c.color}30` }}>
      <Icon className="w-2.5 h-2.5" />
      {c.label}
    </span>
  );
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="text-zinc-600 hover:text-white transition-colors">
      {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

// ─── Tab: Orders ───────────────────────────────────────────────────
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/wallet/admin/orders").then(r => setOrders(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead>
          <tr className="border-b border-white/10 text-zinc-500 font-chakra font-black uppercase tracking-wider">
            <th className="text-left py-2 px-3">ID Comandă</th>
            <th className="text-left py-2 px-3">Utilizator</th>
            <th className="text-right py-2 px-3">Sumă</th>
            <th className="text-right py-2 px-3">Coins</th>
            <th className="text-left py-2 px-3">Metodă</th>
            <th className="text-left py-2 px-3">Status</th>
            <th className="text-left py-2 px-3">Dată</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && (
            <tr><td colSpan={7} className="py-10 text-center text-zinc-600">Nicio comandă înregistrată.</td></tr>
          )}
          {orders.map(o => (
            <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
              <td className="py-2 px-3">
                <div className="flex items-center gap-1 text-amber-400">{o.id} <CopyBtn text={o.id} /></div>
              </td>
              <td className="py-2 px-3 text-white">{o.username}</td>
              <td className="py-2 px-3 text-right text-white font-bold">€{o.amount_eur}</td>
              <td className="py-2 px-3 text-right text-amber-400 font-bold">+{o.total_coins?.toLocaleString()}</td>
              <td className="py-2 px-3 text-zinc-300 uppercase">{o.payment_method}</td>
              <td className="py-2 px-3"><StatusBadge status={o.status} /></td>
              <td className="py-2 px-3 text-zinc-500 text-[10px]">{fmtDate(o.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tab: Transactions ────────────────────────────────────────────
function TransactionsTab() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/wallet/admin/transactions").then(r => setTxs(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead>
          <tr className="border-b border-white/10 text-zinc-500 font-chakra font-black uppercase tracking-wider">
            <th className="text-left py-2 px-3">ID</th>
            <th className="text-left py-2 px-3">Utilizator</th>
            <th className="text-left py-2 px-3">Tip</th>
            <th className="text-right py-2 px-3">Coins</th>
            <th className="text-left py-2 px-3">Descriere</th>
            <th className="text-left py-2 px-3">Dată</th>
          </tr>
        </thead>
        <tbody>
          {txs.length === 0 && (
            <tr><td colSpan={6} className="py-10 text-center text-zinc-600">Nicio tranzacție.</td></tr>
          )}
          {txs.map(tx => {
            const pos = tx.amount_coins > 0;
            return (
              <tr key={tx.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <td className="py-2 px-3 text-zinc-500 text-[10px]">{tx.id}</td>
                <td className="py-2 px-3 text-white">{tx.username}</td>
                <td className="py-2 px-3">
                  <span className="px-1.5 py-0.5 rounded bg-white/5 text-zinc-400 text-[10px] uppercase">{tx.type}</span>
                </td>
                <td className={`py-2 px-3 text-right font-black ${pos ? "text-emerald-400" : "text-rose-400"}`}>
                  {pos ? "+" : ""}{tx.amount_coins?.toLocaleString()}
                </td>
                <td className="py-2 px-3 text-zinc-400 max-w-[200px] truncate">{tx.description}</td>
                <td className="py-2 px-3 text-zinc-500 text-[10px]">{fmtDate(tx.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tab: Vouchers ────────────────────────────────────────────────
function VouchersTab() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ coins: 500, count: 5, expires_days: 30, note: "" });
  const [generated, setGenerated] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/wallet/admin/vouchers").then(r => setVouchers(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/wallet/admin/generate-vouchers", form);
      setGenerated(r.data.vouchers || []);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Eroare la generare.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Generate Form */}
      <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 space-y-3">
        <h4 className="text-xs font-chakra font-black uppercase tracking-widest text-white">Generează Voucher-uri</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[9px] font-chakra font-black uppercase text-zinc-500 mb-1 block">Coins / voucher</label>
            <input type="number" min={1} value={form.coins}
              onChange={e => setForm(f => ({ ...f, coins: +e.target.value }))}
              className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
            />
          </div>
          <div>
            <label className="text-[9px] font-chakra font-black uppercase text-zinc-500 mb-1 block">Cantitate</label>
            <input type="number" min={1} max={50} value={form.count}
              onChange={e => setForm(f => ({ ...f, count: +e.target.value }))}
              className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
            />
          </div>
          <div>
            <label className="text-[9px] font-chakra font-black uppercase text-zinc-500 mb-1 block">Expiră (zile)</label>
            <input type="number" min={1} value={form.expires_days}
              onChange={e => setForm(f => ({ ...f, expires_days: +e.target.value }))}
              className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
            />
          </div>
          <div>
            <label className="text-[9px] font-chakra font-black uppercase text-zinc-500 mb-1 block">Notă</label>
            <input type="text" value={form.note} placeholder="Opțional"
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
            />
          </div>
        </div>
        <button onClick={handleGenerate} disabled={generating}
          className="flex items-center gap-2 px-4 py-2 rounded font-chakra font-black text-xs uppercase text-black disabled:opacity-50"
          style={{ background: "var(--tf-primary)" }}>
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Generează {form.count} Voucher-uri × {form.coins} IDG
        </button>

        {/* Just Generated */}
        {generated.length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar">
            <div className="text-[9px] text-zinc-500 font-chakra uppercase tracking-widest mb-1">Voucher-uri generate:</div>
            {generated.map(v => (
              <div key={v.id} className="flex items-center gap-2 p-2 rounded bg-emerald-500/5 border border-emerald-500/20">
                <span className="font-mono text-xs text-emerald-400 flex-1">{v.pin}</span>
                <span className="text-[10px] text-zinc-500 font-mono">{v.coins?.toLocaleString()} IDG</span>
                <CopyBtn text={v.pin} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Existing Vouchers Table */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-white/10 text-zinc-500 font-chakra font-black uppercase tracking-wider">
                <th className="text-left py-2 px-3">PIN</th>
                <th className="text-right py-2 px-3">Coins</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Utilizat de</th>
                <th className="text-left py-2 px-3">Expiră</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.length === 0 && (
                <tr><td colSpan={5} className="py-10 text-center text-zinc-600">Niciun voucher generat.</td></tr>
              )}
              {vouchers.map(v => (
                <tr key={v.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1.5">
                      <span className={`font-mono ${v.used ? "text-zinc-500 line-through" : "text-amber-400"}`}>{v.pin}</span>
                      {!v.used && <CopyBtn text={v.pin} />}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right text-white font-bold">{v.coins?.toLocaleString()}</td>
                  <td className="py-2 px-3">
                    {v.used
                      ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-700/50 text-zinc-500 border border-white/5">UTILIZAT</span>
                      : <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">ACTIV</span>
                    }
                  </td>
                  <td className="py-2 px-3 text-zinc-400">{v.used_by_username || "—"}</td>
                  <td className="py-2 px-3 text-zinc-500 text-[10px]">{fmtDate(v.expires_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Grant Coins ─────────────────────────────────────────────
function GrantCoinsTab() {
  const [form, setForm] = useState({ user_id: "", amount: 100, reason: "Admin Grant" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleGrant = async () => {
    if (!form.user_id.trim()) { setError("ID-ul utilizatorului este obligatoriu."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await api.post("/wallet/admin/grant-coins", form);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Eroare la creditare.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md space-y-4">
      <h4 className="text-xs font-chakra font-black uppercase tracking-widest text-white">Acordă IDG Coins Manual</h4>
      {error && (
        <div className="flex items-center gap-2 p-3 rounded bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs font-mono">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}
      {result && (
        <div className="flex items-center gap-2 p-3 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          Succes! Am creditat {result.amount?.toLocaleString()} IDG Coins utilizatorului.
        </div>
      )}
      <div className="space-y-3">
        <div>
          <label className="text-[10px] font-chakra font-black uppercase text-zinc-500 mb-1 block">ID Utilizator</label>
          <input type="text" value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))}
            placeholder="ID utilizator din baza de date"
            className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
          />
        </div>
        <div>
          <label className="text-[10px] font-chakra font-black uppercase text-zinc-500 mb-1 block">Cantitate IDG Coins</label>
          <input type="number" min={1} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: +e.target.value }))}
            className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
          />
        </div>
        <div>
          <label className="text-[10px] font-chakra font-black uppercase text-zinc-500 mb-1 block">Motiv</label>
          <input type="text" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
            className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
          />
        </div>
        <button onClick={handleGrant} disabled={loading}
          className="flex items-center gap-2 px-5 py-2.5 rounded font-chakra font-black text-xs uppercase text-black disabled:opacity-50"
          style={{ background: "var(--tf-primary)" }}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coins className="w-3.5 h-3.5" />}
          Acordă {form.amount?.toLocaleString()} IDG Coins
        </button>
      </div>
    </div>
  );
}

// ─── Tab: Payment Method Settings ─────────────────────────────────
function PaymentSettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("card");
  const [msg, setMsg] = useState({ text: "", type: "" });

  useEffect(() => {
    api.get("/wallet/admin/settings")
      .then(r => setSettings(r.data))
      .catch(() => setSettings({}))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg({ text: "", type: "" });
    try {
      await api.post("/wallet/admin/settings", settings);
      setMsg({ text: "Setările metodelor de plată au fost salvate cu succes!", type: "success" });
    } catch (e) {
      setMsg({ text: e.response?.data?.detail || "Eroare la salvarea setărilor.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const updateMethodField = (method, field, value) => {
    setSettings(prev => ({
      ...prev,
      [method]: {
        ...(prev?.[method] || {}),
        [field]: value
      }
    }));
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  const SUB_TABS = [
    { id: "card",    label: "Card Bancar",     icon: CreditCard, color: "#3b82f6" },
    { id: "paypal",  label: "PayPal",          icon: Wallet,     color: "#0079C1" },
    { id: "revolut", label: "Revolut",         icon: Smartphone, color: "#ee1177" },
    { id: "crypto",  label: "Crypto",          icon: Bitcoin,    color: "#f59e0b" },
    { id: "paysafe", label: "PaysafeCard",     icon: Gift,       color: "#f97316" },
    { id: "general", label: "Rate & Banner",   icon: Settings,   color: "#10b981" },
  ];

  return (
    <div className="space-y-6">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between pb-3 border-b border-white/10">
        <div>
          <h3 className="font-chakra font-black text-sm uppercase tracking-wider text-white">Configurare Metode de Plată & Gateway-uri</h3>
          <p className="text-[11px] font-mono text-zinc-400">Configurează gateway-urile, adresele de primire fonduri și comisioanele</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-chakra font-black text-xs uppercase tracking-wider text-black transition-all hover:brightness-110 disabled:opacity-50 shadow-[0_0_15px_rgba(255,79,0,0.3)]"
          style={{ background: "var(--tf-primary)" }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Se salvează..." : "Salvează Toate Setările"}
        </button>
      </div>

      {msg.text && (
        <div className={`p-3 rounded-lg flex items-center gap-2 text-xs font-mono ${
          msg.type === "success" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400" : "bg-rose-500/10 border border-rose-500/30 text-rose-400"
        }`}>
          {msg.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {msg.text}
        </div>
      )}

      {/* Sub tabs for each payment method */}
      <div className="flex gap-2 border-b border-white/10 pb-2 overflow-x-auto">
        {SUB_TABS.map(t => {
          const Icon = t.icon;
          const isSelected = activeSubTab === t.id;
          const isEnabled = t.id === "general" ? true : settings?.[t.id]?.enabled !== false;
          return (
            <button
              key={t.id}
              onClick={() => setActiveSubTab(t.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-chakra font-black uppercase tracking-wider transition-all shrink-0 ${
                isSelected
                  ? "bg-white/10 text-white border border-white/20 shadow-sm"
                  : "bg-white/[0.02] text-zinc-400 hover:text-zinc-200 border border-transparent"
              }`}
            >
              <Icon className="w-3.5 h-3.5" style={{ color: t.color }} />
              {t.label}
              {t.id !== "general" && (
                <span className={`w-2 h-2 rounded-full ${isEnabled ? "bg-emerald-400 shadow-[0_0_6px_#34d399]" : "bg-zinc-600"}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Method Settings Form Content */}
      <div className="bg-black/30 border border-white/10 rounded-xl p-5 space-y-5">
        
        {/* ===== CARD SETTINGS ===== */}
        {activeSubTab === "card" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <div className="flex items-center gap-2.5">
                <CreditCard className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="font-chakra font-black text-white text-xs uppercase">Plată cu Cardul (Visa / Mastercard)</div>
                  <div className="text-[10px] font-mono text-zinc-400">Activează sau dezactivează plățile directe prin card bancar</div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.card?.enabled !== false}
                  onChange={e => updateMethodField("card", "enabled", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Nume Afișat</label>
                <input
                  type="text"
                  value={settings?.card?.label || "Card Bancar"}
                  onChange={e => updateMethodField("card", "label", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Mod Procesare Card</label>
                <select
                  value={settings?.card?.mode || "in_app"}
                  onChange={e => updateMethodField("card", "mode", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="in_app">In-App 3DS Checkout (Direct & Automat)</option>
                  <option value="stripe_link">Link Extern Stripe Payment Link</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Stripe Public Key (Opțional)</label>
                <input
                  type="text"
                  value={settings?.card?.stripe_public_key || ""}
                  placeholder="pk_live_..."
                  onChange={e => updateMethodField("card", "stripe_public_key", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Stripe Secret Key (Opțional)</label>
                <input
                  type="password"
                  value={settings?.card?.stripe_secret_key || ""}
                  placeholder="sk_live_..."
                  onChange={e => updateMethodField("card", "stripe_secret_key", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Comision Afișat</label>
              <input
                type="text"
                value={settings?.card?.fee || "0%"}
                onChange={e => updateMethodField("card", "fee", e.target.value)}
                className="w-48 bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* ===== PAYPAL SETTINGS ===== */}
        {activeSubTab === "paypal" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#0079C1]/5 border border-[#0079C1]/20">
              <div className="flex items-center gap-2.5">
                <Wallet className="w-5 h-5 text-[#0079C1]" />
                <div>
                  <div className="font-chakra font-black text-white text-xs uppercase">Plată PayPal</div>
                  <div className="text-[10px] font-mono text-zinc-400">Primește plăți directe prin contul tău PayPal sau PayPal.Me</div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.paypal?.enabled !== false}
                  onChange={e => updateMethodField("paypal", "enabled", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0079C1]"></div>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Email Cont PayPal</label>
                <input
                  type="email"
                  value={settings?.paypal?.email || "payments@indungi.pro"}
                  onChange={e => updateMethodField("paypal", "email", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#0079C1]"
                />
              </div>
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Link PayPal.Me</label>
                <input
                  type="text"
                  value={settings?.paypal?.paypal_me || "https://paypal.me/indungi"}
                  onChange={e => updateMethodField("paypal", "paypal_me", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#0079C1]"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Comision Afișat</label>
              <input
                type="text"
                value={settings?.paypal?.fee || "0%"}
                onChange={e => updateMethodField("paypal", "fee", e.target.value)}
                className="w-48 bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#0079C1]"
              />
            </div>
          </div>
        )}

        {/* ===== REVOLUT SETTINGS ===== */}
        {activeSubTab === "revolut" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#ee1177]/5 border border-[#ee1177]/20">
              <div className="flex items-center gap-2.5">
                <Smartphone className="w-5 h-5 text-[#ee1177]" />
                <div>
                  <div className="font-chakra font-black text-white text-xs uppercase">Plată Revolut & Revolut.Me</div>
                  <div className="text-[10px] font-mono text-zinc-400">Primește transferuri instantanee prin Revolut Revtag sau link</div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.revolut?.enabled !== false}
                  onChange={e => updateMethodField("revolut", "enabled", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ee1177]"></div>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Revolut Handle / Revtag</label>
                <input
                  type="text"
                  value={settings?.revolut?.handle || "@indungi"}
                  onChange={e => updateMethodField("revolut", "handle", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#ee1177]"
                />
              </div>
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Link Revolut.Me</label>
                <input
                  type="text"
                  value={settings?.revolut?.revolut_me || "https://revolut.me/indungi"}
                  onChange={e => updateMethodField("revolut", "revolut_me", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#ee1177]"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Comision Afișat</label>
              <input
                type="text"
                value={settings?.revolut?.fee || "0%"}
                onChange={e => updateMethodField("revolut", "fee", e.target.value)}
                className="w-48 bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#ee1177]"
              />
            </div>
          </div>
        )}

        {/* ===== CRYPTO SETTINGS ===== */}
        {activeSubTab === "crypto" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <div className="flex items-center gap-2.5">
                <Bitcoin className="w-5 h-5 text-amber-400" />
                <div>
                  <div className="font-chakra font-black text-white text-xs uppercase">Plată Crypto (USDT TRC20 / BTC / ETH)</div>
                  <div className="text-[10px] font-mono text-zinc-400">Adrese de portofel pentru depuneri crypto</div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.crypto?.enabled !== false}
                  onChange={e => updateMethodField("crypto", "enabled", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>

            <div>
              <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Adresă USDT (TRC-20 / Tron)</label>
              <input
                type="text"
                value={settings?.crypto?.usdt_trc20_address || "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t"}
                onChange={e => updateMethodField("crypto", "usdt_trc20_address", e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-amber-400 focus:outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Adresă Bitcoin (BTC)</label>
              <input
                type="text"
                value={settings?.crypto?.btc_address || "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh"}
                onChange={e => updateMethodField("crypto", "btc_address", e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-amber-400 focus:outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Adresă Ethereum (ETH / ERC-20)</label>
              <input
                type="text"
                value={settings?.crypto?.eth_address || "0x71C25828F796eb0ec2F0f60714EAC574A928976F"}
                onChange={e => updateMethodField("crypto", "eth_address", e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-amber-400 focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>
        )}

        {/* ===== PAYSAFECARD SETTINGS ===== */}
        {activeSubTab === "paysafe" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#f97316]/5 border border-[#f97316]/20">
              <div className="flex items-center gap-2.5">
                <Gift className="w-5 h-5 text-[#f97316]" />
                <div>
                  <div className="font-chakra font-black text-white text-xs uppercase">PaysafeCard</div>
                  <div className="text-[10px] font-mono text-zinc-400">Validare PIN PaysafeCard pentru încărcare rapidă</div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.paysafe?.enabled !== false}
                  onChange={e => updateMethodField("paysafe", "enabled", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#f97316]"></div>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Mod Procesare</label>
                <select
                  value={settings?.paysafe?.mode || "instant_auto"}
                  onChange={e => updateMethodField("paysafe", "mode", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#f97316]"
                >
                  <option value="instant_auto">Validare Automată & Creditare Instantă</option>
                  <option value="manual_review">În Așteptare (Verificare Manuală Admin)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Comision Afișat</label>
                <input
                  type="text"
                  value={settings?.paysafe?.fee || "0%"}
                  onChange={e => updateMethodField("paysafe", "fee", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#f97316]"
                />
              </div>
            </div>
          </div>
        )}

        {/* ===== GENERAL SETTINGS ===== */}
        {activeSubTab === "general" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Rată Conversie (IDG Coins per 1 EUR)</label>
                <input
                  type="number"
                  min={1}
                  value={settings?.general?.exchange_rate_coins_per_eur || 100}
                  onChange={e => updateMethodField("general", "exchange_rate_coins_per_eur", +e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                />
                <span className="text-[9px] text-zinc-500 font-mono mt-1 block">Implicit: 1 EUR = 100 IDG Coins</span>
              </div>
              <div>
                <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Monedă Principală</label>
                <input
                  type="text"
                  value={settings?.general?.currency || "EUR"}
                  onChange={e => updateMethodField("general", "currency", e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-emerald-500 uppercase"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-chakra font-black uppercase text-zinc-400 block mb-1">Text Banner Promoțional (Opțional)</label>
              <input
                type="text"
                value={settings?.general?.promo_banner_text || "🎉 OFERTĂ SPECIALĂ: Până la +60% IDG Coins bonus la pachetele mari!"}
                onChange={e => updateMethodField("general", "promo_banner_text", e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────
const TABS = [
  { id: "orders",       label: "Comenzi",          icon: Receipt },
  { id: "transactions", label: "Tranzacții",       icon: Coins },
  { id: "vouchers",     label: "Voucher-uri",      icon: Gift },
  { id: "grant",        label: "Acordă Coins",     icon: Shield },
  { id: "settings",     label: "Metode de Plată",  icon: Settings },
];

export default function AdminWallet() {
  const [tab, setTab] = useState("orders");

  return (
    <div className="space-y-5" data-testid="admin-wallet">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Coins className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <h2 className="font-chakra font-black text-sm uppercase tracking-widest text-white">Gestionare Portofel & Plăți</h2>
            <p className="text-[10px] font-mono text-zinc-500">Comenzi, tranzacții, voucher-uri, creditare manuală și setări gateway-uri</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10 pb-0 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-chakra font-black uppercase tracking-wider border-b-2 transition-all -mb-[1px] shrink-0 ${
                tab === t.id
                  ? "border-[var(--tf-primary)] text-white bg-white/[0.04]"
                  : "border-transparent text-zinc-500 hover:text-zinc-300 hover:border-zinc-500"
              }`}>
              <Icon className="w-3 h-3" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="bg-[#0a0c13] border border-white/10 rounded-lg p-5 min-h-[350px]">
        {tab === "orders"       && <OrdersTab />}
        {tab === "transactions" && <TransactionsTab />}
        {tab === "vouchers"     && <VouchersTab />}
        {tab === "grant"        && <GrantCoinsTab />}
        {tab === "settings"     && <PaymentSettingsTab />}
      </div>
    </div>
  );
}
